/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { completedClusterAction } from '@app/common/rxjs-operators/completed-cluster-action';
import { downloadResult } from '@app/common/utils/download-result';
import { ResourceService } from '@app/core/services/resource.service';
import { buildToken } from '@common/utils/authToken';
import {
  ChangeTdeMasterKeyArgument,
  Cluster2xConnectionToken,
  ClusterCredentials,
  ClusterHealthResponseBody,
  ClusterId,
  ClusterInfo,
  ClusterInfoResponse,
  ClusterUserName2x,
  ShareClusterRequestBody,
  ShareClusterResponse,
  ToggleClusterFeaturesBody,
} from '@shared/types/cluster';
import { ActionName, ClusterLicenseText, ServiceName2x } from '@shared/types/cluster-actions';
import { Cluster3xConnectionToken, HTTP_API_3X_AUTH_HEADER_NAME } from '@shared/types/cluster3x';
import { RemoteReceiverConnections } from '@shared/types/dr';
import { ToggleClusterSuspendedStateRequestBody } from '@shared/types/nebula';
import { NodeId, TopologySnapshot } from '@shared/types/node';
import { TeamId } from '@shared/types/team';
import { Observable } from 'rxjs';
import { pluck } from 'rxjs/operators';
import { ClusterActionService } from './cluster-action.service';
import { WebSocketConnection } from './websocket-connection.service';

@Injectable({ providedIn: 'root' })
export class Clusters {
  constructor(
    private http: HttpClient,
    private ws: WebSocketConnection,
    private clusterActions: ClusterActionService,
    private resourceService: ResourceService,
  ) {}

  private generateAuthHeaders = (credentials?: ClusterCredentials) =>
    credentials && !credentials.useAccessToken
      ? {
          headers: {
            [HTTP_API_3X_AUTH_HEADER_NAME]: buildToken(credentials.login, credentials.password),
          },
        }
      : undefined;

  getClusterByToken(
    connectionToken: Cluster2xConnectionToken | Cluster3xConnectionToken,
    credentials?: ClusterCredentials,
  ) {
    const options = this.generateAuthHeaders(credentials);
    return this.http.get<ClusterInfoResponse>(`/api/v1/clusters/${connectionToken}`, options);
  }
  attach(connectionToken: Cluster2xConnectionToken | Cluster3xConnectionToken, credentials?: ClusterCredentials) {
    const options = this.generateAuthHeaders(credentials);
    return this.http.put<ClusterInfo>(`/api/v1/clusters/${connectionToken}`, {}, options);
  }
  clusterTopology(clusterId: string) {
    return this.ws.getTopic<TopologySnapshot>(`/topic/cluster/topology/${clusterId}`);
  }
  activate(clusterId: string) {
    return this.clusterActions.dispatch(ActionName.CLUSTER_ACTIVATE, { clusterId }).pipe(completedClusterAction());
  }
  deactivate(clusterId: string) {
    return this.clusterActions.dispatch(ActionName.CLUSTER_DEACTIVATE, { clusterId }).pipe(completedClusterAction());
  }
  changeTdeMasterKey(clusterId: ClusterId, masterKey: ChangeTdeMasterKeyArgument) {
    return this.clusterActions
      .dispatch(ActionName.CHANGE_TDE_MASTER_KEY, { clusterId, argument: masterKey })
      .pipe(completedClusterAction());
  }
  removeClusterFromTeam(clusterId: ClusterId, teamId: TeamId): Observable<void> {
    return this.http.delete<void>(`/api/v1/clusters/${clusterId}/teams/${teamId}`);
  }
  changeTag(clusterId: ClusterId, tag: string) {
    return this.clusterActions
      .dispatch(ActionName.CHANGE_TAG, { clusterId, argument: { tag } })
      .pipe(completedClusterAction());
  }
  getUsers(clusterId: ClusterId) {
    return this.clusterActions
      .dispatch(ActionName.LIST_CLUSTER_USERS, { clusterId })
      .pipe(completedClusterAction(), pluck('result'));
  }
  addUser(clusterId: ClusterId, username: ClusterUserName2x, password: string) {
    return this.clusterActions
      .dispatch(ActionName.ADD_CLUSTER_USER, { clusterId, argument: { login: username, password } })
      .pipe(completedClusterAction());
  }
  removeUser(clusterId: ClusterId, username: ClusterUserName2x) {
    return this.clusterActions
      .dispatch(ActionName.REMOVE_CLUSTER_USER, { clusterId, argument: username })
      .pipe(completedClusterAction());
  }
  toggleClusterSuspendedState(clusterId: ClusterId, isSuspended: boolean) {
    return this.http.put(`/api/v1/nebula/deployments/${clusterId}`, {
      suspended: isSuspended,
    } as ToggleClusterSuspendedStateRequestBody);
  }
  toggleClusterFeatures(clusterId: ClusterId, features: ToggleClusterFeaturesBody) {
    return this.http.put(`/api/v1/admin/clusters/${clusterId}`, features);
  }

  shareCluster(clusterId: ClusterId, names: string[]): Observable<ShareClusterResponse> {
    return this.http.post<ShareClusterResponse>(`/api/v1/clusters/${clusterId}/share`, {
      names,
    } as ShareClusterRequestBody);
  }
  getHealth(clusterId: ClusterId) {
    return this.http.get<ClusterHealthResponseBody>(`/api/v1/clusters/${clusterId}/health/reports`);
  }

  exportLogs(clusterId: ClusterId, date: Date) {
    return this.clusterActions
      .dispatch(ActionName.CLUSTER_EXPORT_LOGS, { clusterId, argument: +date })
      .pipe(completedClusterAction());
  }

  downloadExportedLogs(resourceId: string, date: Date) {
    return this.resourceService
      .getResource(resourceId)
      .pipe(downloadResult(`node_logs_${date.toLocaleDateString()}.zip`, 'application/octet-stream'));
  }

  remoteReceiverConnections(clusterId: ClusterId): Observable<RemoteReceiverConnections> {
    return this.ws.getTopic<RemoteReceiverConnections>(`/topic/cluster/${clusterId}/dr/sender/remote-connections`);
  }

  updateLicense(clusterId: ClusterId, license: ClusterLicenseText) {
    const EXECUTE_ON_ALL_NODES: NodeId[] = [];
    return this.clusterActions.dispatch(ActionName.UPDATE_LICENSE, {
      clusterId,
      argument: { license },
      nodeIds: EXECUTE_ON_ALL_NODES,
    });
  }

  stopService(clusterId: ClusterId, serviceName: ServiceName2x) {
    return this.clusterActions
      .dispatch(ActionName.SERVICES_STOP, {
        clusterId,
        argument: [serviceName],
      })
      .pipe(completedClusterAction());
  }
}
